import _ from 'lodash';
import React from 'react';
import DatePicker from '../DatePicker';
import PhoneNumberInput from '../PhoneNumberInput';
import classnames from 'classnames';
import { EMAIL_REGEX } from '../../utility/utility';
import Select from '../../components/Select';
import './styles.scss';

// Note: we show an asterisk next to some field names even though those fields
// aren't truly required for the form to submit. These are the truly required ones.
const REQUIRED_FIELDS = [
  'firstName',
  'lastName',
  'floorplans',
  'emailAddress',
  'phoneNumber',
  'maxBudget',
];

const DEFAULT_DATA = {
  firstName: '',
  lastName: '',
  emailAddress: '',
  phoneNumber: '',
  floorplans: {
    studio: false,
    '1-bedroom': false,
    '1-bedroom-home-office': false,
    '2-bedroom': false,
  },
  maxBudget: null,
  moveInDate: null,
  comments: '',
  isBroker: null,
  terraced: false,
};

/**
 * This component represents a form used to collect the info needed for a
 * nestio "client" object. See http://developers.nestio.com/api/v1/clients.html
 * It is used in the contact form and the schedule a tour form, at least.
 *
 * It stores the form input values and errors as local state. Getting the data
 * out is imperative. The parent uses a ref to trigger validation or ask for the
 * data. This is not idiomatic react, and it violates unidirectional data flow,
 * but it's more ergonomic for how we're going to be using this component than
 * having every parent component hold all this component's state. It just needs
 * to get that state on submit and merge it along.
 */
export default class ClientInfoFields extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      values: { ...DEFAULT_DATA },
      errors: {},

      // For most fields, we keep a ref to them and call focus directly
      // on the dom node to focus them (e.g, after validation fails).
      // However, we can't do that for DatePicker, which takes whether it
      // should be focused as a prop. So we store that here...ugly as it is.
      moveInDateFocused: false,
    };

    this.hasError = this.hasError.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.getData = this.getData.bind(this);
    this.validate = this.validate.bind(this);
    this.validateField = this.validateField.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (
      nextProps.submissionStatus === 'success' &&
      nextProps.resetOnSubmissionSuccess
    ) {
      this.setState({ values: { ...DEFAULT_DATA } });
    }
  }

  handleChange(valuesStateChange) {
    this.setState({
      values: {
        ...this.state.values,
        ...valuesStateChange,
      },
    });
  }

  validateField(field) {
    // Email
    if (field === 'emailAddress') {
      if (!EMAIL_REGEX.test(this.state.values[field])) {
        const error = new Error('invalid email');
        error.field = field;
        throw error;
      }
    }

    // Phone number. Allow up to 14 characters in case there's a country code.
    if (field === 'phoneNumber') {
      if (
        !this.state.values[field].length ||
        this.state.values[field].length < 10 ||
        this.state.values[field].length > 14
      ) {
        const error = new Error('invalid phone number');
        error.field = field;
        throw error;
      }
    }

    // Existence
    if (!this.state.values[field]) {
      const error = new Error('required');
      error.field = field;
      throw error;
    } else {
      this.setState({
        errors: _.omit(this.state.errors, field),
      });
    }
  }

  checkboxClick() {
    this.setState({
      errors: _.omit(this.state.errors, 'floorplans'),
    });
  }

  validateCheckbox() {
    const floorplans = this.state.values.floorplans;
    if (
      floorplans['studio'] === false &&
      floorplans['1-bedroom'] === false &&
      floorplans['2-bedroom'] === false
    ) {
      const error = new Error('must select');
      error.field = 'floorplans';
      this.setState({
        errors: {
          ...this.state.errors,
          floorplans: error.message,
        },
      });
      throw error;
    } else {
      console.log(_.omit(this.state.errors, 'floorplans'));
      this.setState({
        errors: _.omit(this.state.errors, 'floorplans'),
      });
    }

    if (this.state.values.isBroker === null) {
      const error = new Error("Please select whether you are a broker or not.");
      error.field = "isBroker";
      this.setState({
        errors: {
          ...this.state.errors,
          isBroker: error.message,
        },
      });
      throw error;
    } else {
      this.setState({
        errors: _.omit(this.state.errors, "isBroker"),
      });
    }
  }

  validate({ field, autoFocus }) {
    try {
      if (field) {
        this.validateField(field);
      } else {
        this.validateCheckbox();
        REQUIRED_FIELDS.forEach(this.validateField);
      }
    } catch (e) {
      this.setState(
        {
          errors: {
            ...this.state.errors,
            [e.field]: e.message,
          },
          // Focus moveInDate if it's in error and we are supposed to autofocus.
          moveInDateFocused:
            e.field === 'moveInDate' ? autoFocus : this.state.moveInDateFocused,
        },
        // Focus any other field that's in error (if we're supposed to autoFocus).
        // See comment in the constructor about why we handle this differently than moveInDate.
        () =>
          autoFocus &&
          e.field !== 'moveInDate' &&
          this.refs[e.field] &&
          this.refs[e.field].focus()
      );
      return false;
    }
    return true;
  }

  handleBlur(field) {
    if (this.props.validateOnBlur) {
      this.validate({ field });
    }
  }

  getData() {
    return this.state.values;
  }

  hasError(field) {
    return Boolean(this.state.errors[field]);
  }

  render() {
    const floorplanError = this.hasError('floorplans');
    const brokerError = this.hasError('isBroker')
    return (
      <div role="dialog" className="client-info-fields">
        <input
          ref="firstName"
          type="text"
          aria-label="First name: required"
          placeholder="First name *"
          className={classnames({
            'form-control': true,
            error: this.hasError('firstName'),
          })}
          value={this.state.values.firstName}
          onChange={(e) => this.handleChange({ firstName: e.target.value })}
          onBlur={() => this.handleBlur('firstName')}
          maxLength="40"
          autoFocus
        />
        <input
          ref="lastName"
          type="text"
          aria-label="Last name: required"
          placeholder="Last name *"
          className={classnames({
            'form-control': true,
            error: this.hasError('lastName'),
          })}
          value={this.state.values.lastName}
          onChange={(e) => this.handleChange({ lastName: e.target.value })}
          onBlur={() => this.handleBlur('lastName')}
          maxLength="40"
        />
        <input
          ref="emailAddress"
          type="email"
          aria-label="Email: required"
          placeholder="Email address *"
          className={classnames({
            'form-control': true,
            error: this.hasError('emailAddress'),
          })}
          value={this.state.values.emailAddress}
          onChange={(e) => this.handleChange({ emailAddress: e.target.value })}
          onBlur={() => this.handleBlur('emailAddress')}
          maxLength="100"
        />
        <PhoneNumberInput
          ref="phoneNumber"
          aria-label="Phone Number: required"
          placeholder="Phone number *"
          className={classnames({
            'form-control': true,
            error: this.hasError('phoneNumber'),
          })}
          value={this.state.values.phoneNumber}
          onChange={(newValue) => this.handleChange({ phoneNumber: newValue })}
          onBlur={() => this.handleBlur('phoneNumber')}
          maxLength="20"
        />
        <div className="client-info-fields__form-group">
          <p className={'label'}>Preferred floorplans *</p>
          {floorplanError && (
            <p className="error-label">Please Select a Floorplan</p>
          )}
          <div className="checkbox-inline">
            <label>
              <input
                type="checkbox"
                value="studio"
                aria-label="Prefered Floorplan: Studio"
                checked={this.state.values.floorplans.studio}
                onClick={() => this.checkboxClick()}
                onChange={(e) =>
                  this.handleChange({
                    floorplans: {
                      ...this.state.values.floorplans,
                      studio: e.target.checked,
                    },
                  })
                }
              />
              Studio
            </label>
          </div>
          <div className="checkbox-inline">
            <label>
              <input
                type="checkbox"
                value="1-bedroom"
                aria-label="Prefered Floorplan: One Bedroom"
                checked={this.state.values.floorplans['1-bedroom']}
                onClick={() => this.checkboxClick()}
                onChange={(e) =>
                  this.handleChange({
                    floorplans: {
                      ...this.state.values.floorplans,
                      '1-bedroom': e.target.checked,
                    },
                  })
                }
              />
              1 Bedroom
            </label>
          </div>
          <div className="checkbox-inline">
            <label>
              <input
                type="checkbox"
                value="2-bedroom"
                aria-label="Prefered Floorplan: Two Bedroom"
                checked={this.state.values.floorplans['2-bedroom']}
                onClick={() => this.checkboxClick()}
                onChange={(e) =>
                  this.handleChange({
                    floorplans: {
                      ...this.state.values.floorplans,
                      '2-bedroom': e.target.checked,
                    },
                  })
                }
              />
              2 Bedroom
            </label>
          </div>
        </div>
        {this.props.options ? (
          <div>
            <Select
              options={this.props.options}
              ref="maxBudget"
              placeholder="Max. budget (per month) *"
              waitlistClassName={classnames({
                'form-control': true,
                error: this.hasError('maxBudget'),
              })}
              value={
                this.state.values.maxBudget == null
                  ? ''
                  : '$' + this.state.values.maxBudget
              }
              onSelect={(e) => {
                this.handleChange({
                  maxBudget: e.id || null,
                });
              }}
              onBlur={() => this.handleBlur('maxBudget')}
              waitlist={true}
              noSelectionText={(() => {
                return 'Max. budget (per month) *';
              })()}
              asFormControl={true}
              required={true}
            />
          </div>
        ) : (
          <input
            ref="maxBudget"
            type="text"
            aria-label="Max Budget (per month): Required"
            placeholder="Max. budget (per month) *"
            className={classnames({
              'form-control': true,
              error: this.hasError('maxBudget'),
            })}
            value={
              this.state.values.maxBudget == null
                ? ''
                : '$' + this.state.values.maxBudget
            }
            onChange={(e) =>
              this.handleChange({
                maxBudget: e.target.value.replace(/^\$/, '') || null,
              })
            }
            onBlur={() => this.handleBlur('maxBudget')}
            maxLength="7"
          />
        )}

        <DatePicker
          placeholder="Move in Date * mm/dd/yyyy"
          id="moveInDateInput"
          required={true}
          date={this.state.values.moveInDate}
          focused={this.state.moveInDateFocused}
          onFocusChange={({ focused }) => {
            if (focused === false) this.handleBlur('moveInDate');

            this.setState({ moveInDateFocused: focused });
          }}
          onDateChange={(moveInDate) => {
            this.handleChange({ moveInDate });
          }}
          numberOfMonths={1}
          hideKeyboardShortcutsPanel={true}
        />
        <input
          ref="comments"
          type="text"
          aria-label="Comments"
          placeholder="Comments (pets, desired amenities, etc.)"
          className={classnames({
            'form-control': true,
            error: this.hasError('comments'),
          })}
          value={this.state.values.comments}
          onChange={(e) => this.handleChange({ comments: e.target.value })}
          onBlur={() => this.handleBlur('comments')}
        />
        <div className="client-info-fields__form-group">
          <div className="checkbox" id="terracedUnits">
            <label>
              <input
                type="checkbox"
                value="I am interested in terraced units"
                aria-label="I am interested in terraced units"
                checked={this.state.values.terraced}
                onChange={(e) =>
                  this.handleChange({ terraced: e.target.checked })
                }
              />
              I am interested in terraced units
            </label>
          </div>
        </div>

        <div className="client-info-fields__form-group">
          <div className="checkbox" id="broker-question">
            <label>Are you a broker?</label>
            <div className="radio-group">
              <label>
                <input
                  type="radio"
                  value="yes"
                  aria-label="Yes"
                  checked={this.state.values.isBroker === true}
                  onClick={() =>
                    this.setState({
                      errors: _.omit(this.state.errors, "isBroker"),
                    })
                  }
                  onChange={() => this.handleChange({ isBroker: true })}
                />
                Yes
              </label>
              <label>
                <input
                  type="radio"
                  value="no"
                  aria-label="No"
                  checked={this.state.values.isBroker === false}
                  onClick={() =>
                    this.setState({
                      errors: _.omit(this.state.errors, "isBroker"),
                    })
                  }
                  onChange={() => this.handleChange({ isBroker: false })}
                />
                No
              </label>
            </div>
            {brokerError && (
              <p
                style={{
                  marginTop: "10px",
                }}
                className="error-label"
              >
                Please select whether you are a broker or not.
              </p>
            )}
          </div>
        </div>
        <div className="broker-info">
          <p
            style={{
              fontSize: "12px",
              lineHeight: "14px",
              paddingBottom: "5px",
            }}
          >
            All Two Trees apartments are No Fee when rented directly from
            landlord without the use of a broker unless otherwise specified.
            Listings and advertisements on this site are for informational
            purposes only and do not create a relationship between a real estate
            licensee or broker and the landlord or managing agent. Listings and
            ads may not be republished.
          </p>
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./client/components/ClientInfoFields/index.js